import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type PanState = {
  x: number;
  y: number;
};

type CanvasToolbarState = {
  zoom: number;
  pan: PanState;
  panEnabled: boolean;
};

type CanvasToolbarDispatch = {
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  resetPan: () => void;
  togglePan: () => void;
  setPanEnabled: (enabled: boolean) => void;
  setPan: React.Dispatch<React.SetStateAction<PanState>>;
  resetView: () => void;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
};

const CanvasToolbarStateContext = createContext<CanvasToolbarState | null>(null);
const CanvasToolbarDispatchContext = createContext<CanvasToolbarDispatch | null>(null);

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 8;
export const CanvasToolbarProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [zoom, setZoom] = useState(window.innerWidth < 768 ? 3 : 1);
  const [pan, setPan] = useState<PanState>({ x: 0, y: 0 });
  const [panEnabled, setPanEnabled] = useState(true);

  const clamp = useCallback((value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)), []);

  const zoomIn = useCallback(() => {
    setZoom((currentZoom) => clamp(currentZoom * 1.1));
  }, [clamp]);

  const zoomOut = useCallback(() => {
    setZoom((currentZoom) => clamp(currentZoom / 1.1));
  }, [clamp]);

  const resetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  const resetPan = useCallback(() => {
    setPan({ x: 0, y: 0 });
  }, []);

  const togglePan = useCallback(() => {
    setPanEnabled((current) => !current);
  }, []);

  const setPanEnabledExplicit = useCallback((enabled: boolean) => {
    setPanEnabled(enabled);
  }, []);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Memoized state value
  const stateValue = useMemo(
    () => ({
      zoom,
      pan,
      panEnabled,
    }),
    [zoom, pan, panEnabled],
  );

  // Memoized dispatch value (functions are stable)
  const dispatchValue = useMemo(
    () => ({
      zoomIn,
      zoomOut,
      resetZoom,
      resetPan,
      togglePan,
      setPanEnabled: setPanEnabledExplicit,
      setPan,
      resetView,
      setZoom
    }),
    [zoomIn, zoomOut, resetZoom, resetPan, togglePan, setPanEnabledExplicit, setPan, resetView],
  );

  return (
    <CanvasToolbarStateContext.Provider value={stateValue}>
      <CanvasToolbarDispatchContext.Provider value={dispatchValue}>{children}</CanvasToolbarDispatchContext.Provider>
    </CanvasToolbarStateContext.Provider>
  );
};

// Create separate hooks for state and dispatch
export const useCanvasToolbarState = () => {
  const context = useContext(CanvasToolbarStateContext);
  if (!context) {
    throw new Error("useCanvasToolbarState must be used within a CanvasToolbarProvider");
  }
  return context;
};

export const useCanvasToolbarDispatch = () => {
  const context = useContext(CanvasToolbarDispatchContext);
  if (!context) {
    throw new Error("useCanvasToolbarDispatch must be used within a CanvasToolbarProvider");
  }
  return context;
};